import { useState, useEffect, useCallback, useMemo, CSSProperties } from 'react';
import { apiFetch } from '../utils/api';

type Status = 'pending' | 'replied' | 'failed' | 'needs_review' | 'dismissed' | 'awaiting_confirmation';

interface CommentRecord {
  postId: string | null;
  commentId: string;
  commenterId: string;
  commenterName: string | null;
  commentText: string;
  receivedAt: number;
  status: Status;
  replyText?: string;
  repliedAt?: number;
  responseTimeMs?: number;
  repliedByHuman?: boolean;
  error?: string;
  failedAt?: number;
  flagReason?: 'hate_speech' | 'political' | 'competitor' | 'other';
  holdingReplyText?: string;
  flaggedAt?: number;
  dismissedAt?: number;
  draftReply?: string;
  draftGeneratedAt?: number;
}

interface PostInfo {
  message?: string;
  permalink_url?: string;
  created_time?: string;
}

const DRAFT_HOLD_MS = 15000;

function fmtDuration(ms: number) {
  const s = Math.max(0, Math.floor(ms / 1000));
  if (s < 60) return `${s}s`;
  const m = Math.floor(s / 60);
  if (m < 60) return `${m}m ${s % 60}s`;
  const h = Math.floor(m / 60);
  return `${h}h ${m % 60}m`;
}

function fmtTime(ts: number) {
  return new Date(ts).toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' });
}

const STATUS_STYLE: Record<Status, { bg: string; color: string; label: string }> = {
  pending:              { bg: '#f59e0b20', color: '#f59e0b', label: '🟡 Waiting on reply' },
  awaiting_confirmation: { bg: '#5B4FE820', color: '#818cf8', label: '⏳ Reviewing before send' },
  replied:              { bg: '#10b98120', color: '#10b981', label: '✅ Replied' },
  failed:               { bg: '#ef444420', color: '#ef4444', label: '❌ Failed to reply' },
  needs_review:         { bg: '#f9731620', color: '#f97316', label: '🚩 Needs review' },
  dismissed:            { bg: '#6b728020', color: '#9ca3af', label: '⚪ Dismissed' },
};

const FLAG_LABELS: Record<string, string> = {
  hate_speech: 'Flagged: hate speech / attack',
  political:   'Flagged: political',
  competitor:  'Flagged: competitor mention',
  other:       'Flagged: needs a human look',
};

interface Props {
  botId: string;
}

const btnBase: CSSProperties = { borderRadius: '7px', padding: '7px 14px', fontSize: '12px', cursor: 'pointer' };

export default function FacebookCommentsPanel({ botId }: Props) {
  const [comments, setComments] = useState<CommentRecord[]>([]);
  const [posts, setPosts] = useState<Record<string, PostInfo>>({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [now, setNow] = useState(Date.now());

  const [drafts, setDrafts] = useState<Record<string, string>>({});
  const [suggestionInputs, setSuggestionInputs] = useState<Record<string, string>>({});
  const [expanded, setExpanded] = useState<Record<string, boolean>>({});
  const [suggesting, setSuggesting] = useState<Record<string, boolean>>({});
  const [regenerating, setRegenerating] = useState<Record<string, boolean>>({});
  const [sending, setSending] = useState<Record<string, boolean>>({});
  const [dismissing, setDismissing] = useState<Record<string, boolean>>({});
  const [retrying, setRetrying] = useState<Record<string, boolean>>({});
  const [refreshing, setRefreshing] = useState(false);
  const [lastLoaded, setLastLoaded] = useState<number | null>(null);

  const load = useCallback(async (manual = false) => {
    if (manual) setRefreshing(true);
    try {
      const res = await apiFetch(`/api/bots/${botId}/facebook-comments`);
      const data = await res.json();
      if (!data.success) throw new Error(data.error || 'Could not load comments');
      setComments(data.comments || []);
      setPosts(data.posts || {});
      setError('');
      setLastLoaded(Date.now());
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not load comments');
    } finally {
      setLoading(false);
      if (manual) setRefreshing(false);
    }
  }, [botId]);

  useEffect(() => { load(); }, [load]);

  // Poll for new comments / status changes every 20s — cheap, and keeps the
  // "waiting on reply" timers honest without the user having to refresh. A
  // comment "awaiting_confirmation" auto-sends on the server after 15s
  // regardless of whether anyone is looking at this tab, so this poll just
  // needs to catch up with that, not drive it.
  useEffect(() => {
    const t = setInterval(load, 20000);
    return () => clearInterval(t);
  }, [load]);

  // Tick every second while anything is time-sensitive, so both the
  // "waiting Xs" and the 15s auto-send countdown move live.
  const hasPending = comments.some(c => c.status === 'pending' || c.status === 'needs_review' || c.status === 'awaiting_confirmation');
  useEffect(() => {
    if (!hasPending) return;
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, [hasPending]);

  const groups = useMemo(() => {
    const byPost = new Map<string, CommentRecord[]>();
    for (const c of comments) {
      const key = c.postId || '(no post id)';
      if (!byPost.has(key)) byPost.set(key, []);
      byPost.get(key)!.push(c);
    }
    return Array.from(byPost.entries()).sort((a, b) => {
      const aLatest = Math.max(...a[1].map(c => c.receivedAt || 0));
      const bLatest = Math.max(...b[1].map(c => c.receivedAt || 0));
      return bLatest - aLatest;
    });
  }, [comments]);

  const pendingCount = comments.filter(c => c.status === 'pending').length;
  const failedCount = comments.filter(c => c.status === 'failed').length;
  const reviewCount = comments.filter(c => c.status === 'needs_review').length;
  const awaitingCount = comments.filter(c => c.status === 'awaiting_confirmation').length;

  function isExpanded(c: CommentRecord) {
    return expanded[c.commentId] ?? (c.status === 'needs_review' || c.status === 'awaiting_confirmation');
  }

  async function suggestReply(c: CommentRecord) {
    setSuggesting(s => ({ ...s, [c.commentId]: true }));
    try {
      const res = await apiFetch(`/api/bots/${botId}/facebook-comments/${c.commentId}/suggest`);
      const data = await res.json();
      if (data.success) setDrafts(d => ({ ...d, [c.commentId]: data.suggestion || '' }));
      else alert(data.error || 'Could not generate a suggestion.');
    } catch {
      alert('Could not generate a suggestion — check your connection.');
    } finally {
      setSuggesting(s => ({ ...s, [c.commentId]: false }));
    }
  }

  async function regenerate(c: CommentRecord) {
    setRegenerating(s => ({ ...s, [c.commentId]: true }));
    try {
      const res = await apiFetch(`/api/bots/${botId}/facebook-comments/${c.commentId}/regenerate`, {
        method: 'POST',
        body: JSON.stringify({ suggestion: suggestionInputs[c.commentId] || '' }),
      });
      const data = await res.json();
      if (!data.success) { alert(data.error || 'Could not regenerate.'); return; }
      setDrafts(d => ({ ...d, [c.commentId]: data.draftReply || '' }));
      setSuggestionInputs(s => ({ ...s, [c.commentId]: '' }));
      load();
    } catch {
      alert('Could not regenerate — check your connection.');
    } finally {
      setRegenerating(s => ({ ...s, [c.commentId]: false }));
    }
  }

  async function sendReply(c: CommentRecord, text: string) {
    const replyText = text.trim();
    if (!replyText) { alert('Write a reply first.'); return; }
    setSending(s => ({ ...s, [c.commentId]: true }));
    try {
      const res = await apiFetch(`/api/bots/${botId}/facebook-comments/${c.commentId}/reply`, {
        method: 'POST',
        body: JSON.stringify({ replyText }),
      });
      const data = await res.json();
      if (!data.success) { alert(data.error || 'Could not send reply.'); return; }
      setDrafts(d => { const n = { ...d }; delete n[c.commentId]; return n; });
      load();
    } catch {
      alert('Could not send reply — check your connection.');
    } finally {
      setSending(s => ({ ...s, [c.commentId]: false }));
    }
  }

  async function dismiss(c: CommentRecord) {
    if (!window.confirm('Dismiss this comment with no further reply?')) return;
    setDismissing(s => ({ ...s, [c.commentId]: true }));
    try {
      const res = await apiFetch(`/api/bots/${botId}/facebook-comments/${c.commentId}/dismiss`, { method: 'POST' });
      const data = await res.json();
      if (!data.success) { alert(data.error || 'Could not dismiss.'); return; }
      load();
    } catch {
      alert('Could not dismiss — check your connection.');
    } finally {
      setDismissing(s => ({ ...s, [c.commentId]: false }));
    }
  }

  async function retry(c: CommentRecord) {
    setRetrying(s => ({ ...s, [c.commentId]: true }));
    try {
      const res = await apiFetch(`/api/bots/${botId}/facebook-comments/${c.commentId}/retry`, { method: 'POST' });
      const data = await res.json();
      if (!data.success) { alert(data.error || 'Could not retry.'); return; }
      load();
    } catch {
      alert('Could not retry — check your connection.');
    } finally {
      setRetrying(s => ({ ...s, [c.commentId]: false }));
    }
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '12px' }}>
        <div style={{ color: 'var(--text-faint)', fontSize: '11px' }}>
          {lastLoaded ? `Updated ${fmtTime(lastLoaded)}` : ''}
        </div>
        <button onClick={() => load(true)} disabled={refreshing}
          style={{ ...btnBase, background: 'var(--bg-element)', border: '1px solid var(--card-border)', color: 'var(--text-secondary)', fontWeight: '600', display: 'flex', alignItems: 'center', gap: '6px', cursor: refreshing ? 'not-allowed' : 'pointer' }}>
          <span style={{ display: 'inline-block', transform: refreshing ? 'rotate(360deg)' : undefined, transition: refreshing ? 'transform 0.8s linear' : undefined }}>↻</span>
          {refreshing ? 'Refreshing…' : 'Refresh'}
        </button>
      </div>

      {(pendingCount > 0 || failedCount > 0 || reviewCount > 0 || awaitingCount > 0) && (
        <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
          {reviewCount > 0 && (
            <div style={{ background: '#f9731620', color: '#f97316', borderRadius: '8px', padding: '8px 14px', fontSize: '12px', fontWeight: '600' }}>
              {reviewCount} flagged — needs your review
            </div>
          )}
          {awaitingCount > 0 && (
            <div style={{ background: '#5B4FE820', color: '#818cf8', borderRadius: '8px', padding: '8px 14px', fontSize: '12px', fontWeight: '600' }}>
              {awaitingCount} reply{awaitingCount > 1 ? 'ies' : ''} ready — review before auto-send
            </div>
          )}
          {pendingCount > 0 && (
            <div style={{ background: '#f59e0b20', color: '#f59e0b', borderRadius: '8px', padding: '8px 14px', fontSize: '12px', fontWeight: '600' }}>
              {pendingCount} waiting on a reply
            </div>
          )}
          {failedCount > 0 && (
            <div style={{ background: '#ef444420', color: '#ef4444', borderRadius: '8px', padding: '8px 14px', fontSize: '12px', fontWeight: '600' }}>
              {failedCount} failed to get a reply
            </div>
          )}
        </div>
      )}

      {loading ? (
        <div style={{ color: 'var(--text-faint)', fontSize: '13px' }}>Loading…</div>
      ) : error ? (
        <div style={{ color: '#ef4444', fontSize: '13px' }}>{error}</div>
      ) : comments.length === 0 ? (
        <div style={{ background: 'var(--bg-card)', borderRadius: '12px', border: '1px solid var(--card-border)', padding: '24px', color: 'var(--text-faint)', fontSize: '13px' }}>
          No comments received yet. When someone comments on one of your Page's posts, it'll show up here.
        </div>
      ) : (
        groups.map(([postId, groupComments]) => {
          const post = posts[postId];
          return (
            <div key={postId} style={{ background: 'var(--bg-card)', borderRadius: '12px', border: '1px solid var(--card-border)', padding: '20px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '14px', gap: '12px' }}>
                <div style={{ color: 'var(--text-secondary)', fontSize: '13px', lineHeight: 1.5 }}>
                  {post?.message
                    ? <span>{post.message.length > 140 ? post.message.slice(0, 140) + '…' : post.message}</span>
                    : <em style={{ color: 'var(--text-faint)' }}>Post {postId !== '(no post id)' ? postId : '(unknown)'}</em>}
                </div>
                {post?.permalink_url && (
                  <a href={post.permalink_url} target="_blank" rel="noreferrer" style={{ color: '#818cf8', fontSize: '12px', whiteSpace: 'nowrap', flexShrink: 0 }}>
                    View post →
                  </a>
                )}
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                {groupComments.map(c => {
                  const style = STATUS_STYLE[c.status];
                  const needsReview = c.status === 'needs_review';
                  const awaiting = c.status === 'awaiting_confirmation';
                  const open = isExpanded(c);
                  const secondsLeft = awaiting && c.draftGeneratedAt
                    ? Math.max(0, Math.ceil((DRAFT_HOLD_MS - (now - c.draftGeneratedAt)) / 1000))
                    : null;
                  const draftValue = drafts[c.commentId] ?? c.draftReply ?? '';

                  return (
                    <div key={c.commentId} style={{ padding: '12px', background: 'var(--bg-page)', borderRadius: '8px', border: (needsReview || awaiting) ? `1px solid ${style.color}60` : '1px solid var(--card-border)' }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '6px', gap: '10px' }}>
                        <div style={{ color: 'var(--text-primary)', fontSize: '13px', fontWeight: '600' }}>
                          {c.commenterName || c.commenterId}
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                          {awaiting && secondsLeft !== null && (
                            <span style={{ color: '#818cf8', fontSize: '11px', fontWeight: '700', whiteSpace: 'nowrap' }}>
                              auto-sending in {secondsLeft}s
                            </span>
                          )}
                          <span style={{ background: style.bg, color: style.color, fontSize: '11px', fontWeight: '700', padding: '3px 10px', borderRadius: '20px', whiteSpace: 'nowrap' }}>
                            {style.label}
                          </span>
                          {(c.status === 'replied' || c.status === 'failed' || c.status === 'dismissed' || c.status === 'pending') && (
                            <button onClick={() => setExpanded(e => ({ ...e, [c.commentId]: !open }))}
                              style={{ background: 'none', border: 'none', color: 'var(--text-faint)', cursor: 'pointer', fontSize: '14px', padding: '2px 4px' }}>
                              {open ? '▾' : '▸'}
                            </button>
                          )}
                        </div>
                      </div>
                      <div style={{ color: 'var(--text-secondary)', fontSize: '13px', marginBottom: '8px', whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                        {c.commentText}
                      </div>

                      {!open && c.status !== 'needs_review' && c.status !== 'awaiting_confirmation' ? null : (
                        <>
                          {awaiting && (
                            <div style={{ marginBottom: '10px' }}>
                              <div style={{ color: 'var(--text-faint)', fontSize: '11px', marginBottom: '6px' }}>
                                This is what the bot is about to post publicly — edit it, regenerate it, send it now, or stop it.
                              </div>
                              <textarea
                                value={draftValue}
                                onChange={e => setDrafts(d => ({ ...d, [c.commentId]: e.target.value }))}
                                rows={3}
                                style={{ width: '100%', padding: '10px', background: 'var(--bg-card)', border: '1px solid var(--card-border)', borderRadius: '8px', color: 'var(--text-primary)', fontSize: '13px', outline: 'none', resize: 'vertical', boxSizing: 'border-box', marginBottom: '8px' }}
                              />
                              <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', marginBottom: '8px' }}>
                                <input
                                  value={suggestionInputs[c.commentId] || ''}
                                  onChange={e => setSuggestionInputs(s => ({ ...s, [c.commentId]: e.target.value }))}
                                  placeholder="Tell the AI what to change (optional), then regenerate…"
                                  style={{ flex: 1, minWidth: '180px', padding: '8px 10px', background: 'var(--bg-card)', border: '1px solid var(--card-border)', borderRadius: '7px', color: 'var(--text-primary)', fontSize: '12px', outline: 'none' }}
                                />
                                <button onClick={() => regenerate(c)} disabled={regenerating[c.commentId]}
                                  style={{ ...btnBase, background: 'var(--bg-element)', border: '1px solid var(--card-border)', color: 'var(--text-secondary)' }}>
                                  {regenerating[c.commentId] ? 'Regenerating…' : '🔄 Regenerate'}
                                </button>
                              </div>
                              <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                                <button onClick={() => sendReply(c, draftValue)} disabled={sending[c.commentId]}
                                  style={{ ...btnBase, background: '#5B4FE8', border: 'none', color: 'white', fontWeight: '600' }}>
                                  {sending[c.commentId] ? 'Sending…' : 'Send now'}
                                </button>
                                <button onClick={() => dismiss(c)} disabled={dismissing[c.commentId]}
                                  style={{ ...btnBase, background: 'none', border: '1px solid var(--card-border)', color: 'var(--text-faint)' }}>
                                  {dismissing[c.commentId] ? '…' : "Don't reply"}
                                </button>
                              </div>
                            </div>
                          )}

                          {needsReview && (
                            <div style={{ marginBottom: '10px' }}>
                              <div style={{ color: '#f97316', fontSize: '11px', fontWeight: '600', marginBottom: '6px' }}>
                                {FLAG_LABELS[c.flagReason || 'other']}
                              </div>
                              {c.holdingReplyText && (
                                <div style={{ borderLeft: '2px solid #f9731660', paddingLeft: '10px', marginBottom: '10px' }}>
                                  <div style={{ color: 'var(--text-faint)', fontSize: '11px', marginBottom: '2px' }}>Bot posted a holding reply</div>
                                  <div style={{ color: 'var(--text-secondary)', fontSize: '12px' }}>{c.holdingReplyText}</div>
                                </div>
                              )}
                              <textarea
                                value={draftValue}
                                onChange={e => setDrafts(d => ({ ...d, [c.commentId]: e.target.value }))}
                                placeholder="Write the real reply to post publicly…"
                                rows={3}
                                style={{ width: '100%', padding: '10px', background: 'var(--bg-card)', border: '1px solid var(--card-border)', borderRadius: '8px', color: 'var(--text-primary)', fontSize: '13px', outline: 'none', resize: 'vertical', boxSizing: 'border-box', marginBottom: '8px' }}
                              />
                              <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                                <button onClick={() => suggestReply(c)} disabled={suggesting[c.commentId]}
                                  style={{ ...btnBase, background: 'var(--bg-element)', border: '1px solid var(--card-border)', color: 'var(--text-secondary)' }}>
                                  {suggesting[c.commentId] ? 'Thinking…' : '✨ Suggest reply'}
                                </button>
                                <button onClick={() => sendReply(c, draftValue)} disabled={sending[c.commentId]}
                                  style={{ ...btnBase, background: '#5B4FE8', border: 'none', color: 'white', fontWeight: '600' }}>
                                  {sending[c.commentId] ? 'Sending…' : 'Send reply'}
                                </button>
                                <button onClick={() => dismiss(c)} disabled={dismissing[c.commentId]}
                                  style={{ ...btnBase, background: 'none', border: '1px solid var(--card-border)', color: 'var(--text-faint)' }}>
                                  {dismissing[c.commentId] ? '…' : 'Dismiss (no reply)'}
                                </button>
                              </div>
                            </div>
                          )}

                          {c.status === 'replied' && c.replyText && (
                            <div style={{ borderLeft: '2px solid #10b98160', paddingLeft: '10px', marginBottom: '6px' }}>
                              <div style={{ color: 'var(--text-faint)', fontSize: '11px', marginBottom: '2px' }}>
                                {c.repliedByHuman ? 'You replied' : 'Bot replied'}
                              </div>
                              <div style={{ color: 'var(--text-secondary)', fontSize: '12px', whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>{c.replyText}</div>
                            </div>
                          )}

                          {c.status === 'failed' && (
                            <div style={{ marginBottom: '6px' }}>
                              {c.error && <div style={{ color: '#ef4444', fontSize: '11px', marginBottom: '8px' }}>{c.error}</div>}
                              <button onClick={() => retry(c)} disabled={retrying[c.commentId]}
                                style={{ ...btnBase, background: '#ef444420', border: '1px solid #ef444440', color: '#ef4444', fontWeight: '600' }}>
                                {retrying[c.commentId] ? 'Retrying…' : '↻ Retry'}
                              </button>
                            </div>
                          )}
                        </>
                      )}

                      <div style={{ color: 'var(--text-faint)', fontSize: '11px' }}>
                        {fmtTime(c.receivedAt)}
                        {c.status === 'pending' && <> · waiting {fmtDuration(now - c.receivedAt)}</>}
                        {c.status === 'replied' && c.responseTimeMs != null && <> · replied in {fmtDuration(c.responseTimeMs)}</>}
                        {needsReview && c.flaggedAt && <> · flagged {fmtDuration(now - c.flaggedAt)} ago</>}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          );
        })
      )}
    </div>
  );
}
